import { EMPTY_GUID } from '../../lib/constants/misc';
import { Box, Container, SxProps, Theme, Button } from '@mui/material';
import CasePoliciesAndClinicalsModal from './Modals/CasePoliciesAndClinicalsModal';
import { useScreenSmallerThan } from '../../lib/hooks/breakpoint-hooks';
import { downloadAuditPagePdf } from '../Audit/ExportPdf/ExportPdf';
import { CaseDetailsModel } from '../../lib/models/CaseDetailsModel';
import { AuditHistoryModel } from '../../lib/models/AuditHistoryModel';
import { CaseMessageModel } from '../../lib/models/CaseMessageModel';
import React, { useMemo } from 'react';
import { navMenuService } from '../../lib/constants/nav-menu-data';
import useIsUserRole from '../../lib/hooks/use-is-user-role';

export type CaseDetailsHeaderProps = {
    displayDueDate: string | undefined;
    displayDueTime: string | undefined;
    caseNumber: string | undefined;
    id: string | undefined;
    caseDetails?: CaseDetailsModel | null;
    crmClient: string | undefined;
    isAudit?: boolean;
    auditHistory?: AuditHistoryModel[];
    caseMessages?: CaseMessageModel[];
    clinicalReviewId: string | undefined;
};

const MemoizedCasePoliciesAndClinicalsModal = React.memo(CasePoliciesAndClinicalsModal);

const CaseDetailsHeader = ({
    displayDueDate,
    displayDueTime,
    caseNumber,
    id,
    crmClient,
    isAudit,
    caseDetails,
    auditHistory,
    caseMessages,
    clinicalReviewId
}: CaseDetailsHeaderProps) => {
    const isSmallerThanSmall = useScreenSmallerThan('sm');

    const allowedRolesForAudit = useMemo(() => {
        const roles = navMenuService.getRolesOfNavItem('/search');
        return roles?.filter((r) => r !== 'user') || [];
    }, []);

    const isRoleAllowed = useIsUserRole(...allowedRolesForAudit);

    const onAuditPdfDownload = async () => {
        if (caseDetails) {
            await downloadAuditPagePdf({ caseDetails, auditHistory, caseMessages });
        }
    };

    const policyAndClinicalModalProps = useMemo(
        () => ({
            caseId: id ?? EMPTY_GUID,
            crmId: crmClient ?? EMPTY_GUID,
            clinicalReviewId: clinicalReviewId ?? EMPTY_GUID
        }),
        [id, crmClient, clinicalReviewId]
    );

    return (
        <>
            <Box
                sx={{
                    backgroundColor: (t) => t.palette.secondary.light,
                    paddingY: '0.625rem',
                    borderBottom: 1,
                    borderBottomWidth: 1
                }}>
                <Container maxWidth={false}>
                    <Box sx={boxStyle(isSmallerThanSmall)}>
                        <Box>
                            <Box sx={{ fontWeight: 'bold', color: (t) => t.palette.primary.main }}>
                                {caseNumber}
                            </Box>
                            {displayDueDate != null ? (
                                <Box sx={{ color: 'white', fontSize: 13 }}>
                                    {displayDueDate} -
                                    <Box sx={{ display: 'inline-block', fontWeight: 'bold' }}>
                                        {displayDueTime + ' EST'}{' '}
                                    </Box>
                                </Box>
                            ) : (
                                ''
                            )}
                        </Box>

                        <Box sx={buttonBoxStyle(isSmallerThanSmall)}>
                            <MemoizedCasePoliciesAndClinicalsModal
                                {...policyAndClinicalModalProps}
                            />
                            {isAudit && isRoleAllowed && (
                                <Button variant="outlined" onClick={onAuditPdfDownload}>
                                    Export PDF
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Container>
            </Box>
        </>
    );
};

const boxStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8
    };
    const smallerThanSm: SxProps<Theme> = {};

    return media ? smallerThanSm : biggerThanSm;
};

const buttonBoxStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = {
        display: 'inline-flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 1
    };
    const smallerThanSm: SxProps<Theme> = { marginTop: '0.75rem' };

    return media ? smallerThanSm : biggerThanSm;
};

export default CaseDetailsHeader;
